import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowDown, ArrowUp, Download, Search } from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '../../Components/ui/badge'
import { Button } from '../../Components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../Components/ui/card'
import { Input } from '../../Components/ui/input'
import { Skeleton } from '../../Components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../Components/ui/table'
import env from '../../env/env'
import { get } from '../../http/restservice'
import { firstUserIdeaDetailPath, getQuestionnaireVersion } from './firstUserIdeaUtils'

const getDateAndTime = (timestamp) => {
  const dateObj = new Date(timestamp)
  if (Number.isNaN(dateObj.getTime())) {
    return { date: '—', time: '' }
  }

  const year = dateObj.getFullYear()
  const month = String(dateObj.getMonth() + 1).padStart(2, '0')
  const day = String(dateObj.getDate()).padStart(2, '0')
  const hours = String(dateObj.getHours()).padStart(2, '0')
  const minutes = String(dateObj.getMinutes()).padStart(2, '0')
  const seconds = String(dateObj.getSeconds()).padStart(2, '0')

  return {
    date: `${year}-${month}-${day}`,
    time: `${hours}:${minutes}:${seconds}`,
  }
}

const SortButton = ({ label, active, onClick }) => (
  <Button variant='ghost' size='sm' className='h-8 gap-1 px-2' onClick={onClick}>
    {label}
    {active ? <ArrowUp className='h-3.5 w-3.5' /> : <ArrowDown className='h-3.5 w-3.5' />}
  </Button>
)

const FirstUserIdea = () => {
  const [users, setUsers] = useState([])
  const [sortState, setSortState] = useState({
    status: true,
    createdOn: true,
    submittedOn: true,
  })
  const [searchQuery, setSearchQuery] = useState('')
  const [isLoading, setIsLoading] = useState(true)

  const filteredUsers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    if (!query) return users

    return users.filter((user) => {
      const fullName = `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase()
      return fullName.includes(query) || String(user.phoneNumber || '').includes(query)
    })
  }, [users, searchQuery])

  const fetchAllUsers = async () => {
    setIsLoading(true)
    try {
      const response = await get(`${env.API_URL}adminservice/api/actionPlan/fetchAllFirstIdeas`)
      const nextUsers = JSON.parse(response?.data)
      setUsers(Array.isArray(nextUsers) ? nextUsers : [])
    } catch (error) {
      toast.error('Unable to load first user ideas')
      setUsers([])
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchAllUsers()
  }, [])

  const handleSortByStatus = () => {
    const sortedUsers = [...users].sort((a, b) => {
      if (sortState.status) {
        return b.isCompleted - a.isCompleted
      }
      return a.isCompleted - b.isCompleted
    })
    setUsers(sortedUsers)
    setSortState({ ...sortState, status: !sortState.status })
  }

  const handleSortByDate = (key) => {
    const sortedUsers = [...users].sort((a, b) => {
      const dateA = new Date(a[key === 'createdOn' ? 'createdTimestamp' : 'updatedTimestamp'])
      const dateB = new Date(b[key === 'createdOn' ? 'createdTimestamp' : 'updatedTimestamp'])
      return sortState[key] ? dateA - dateB : dateB - dateA
    })
    setUsers(sortedUsers)
    setSortState({ ...sortState, [key]: !sortState[key] })
  }

  const downloadAllUsersCSV = () => {
    if (!users || users.length === 0) {
      toast.error('No users available to export')
      return
    }

    const headers = ['ID', 'First Name', 'Last Name', 'Phone Number', 'Status', 'User Response']
    const rows = users.map((user) => [
      user.id,
      user.firstName,
      user.lastName,
      user.phoneNumber,
      user.isCompleted ? 'Completed' : 'Pending',
      user.userResponse ? JSON.stringify(user.userResponse) : 'N/A',
    ])

    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [headers.join(','), ...rows.map((row) => row.join(','))].join('\n')

    const encodedUri = encodeURI(csvContent)
    const link = document.createElement('a')
    link.setAttribute('href', encodedUri)
    link.setAttribute('download', 'all_users.csv')
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <div className='mx-auto w-full max-w-none px-4 pb-8 pt-4 md:px-6 lg:px-8'>
      <div className='mb-5 md:mb-6'>
        <p className='mb-1 text-xs font-medium uppercase tracking-[0.08em] text-[var(--fb-text-muted)]'>
          Admin tools
        </p>
        <h1 className='font-IBM text-2xl font-bold tracking-tight text-[var(--fb-text-strong)] md:text-3xl'>
          First User Ideas
        </h1>
        <p className='mt-1 text-sm text-[var(--fb-text-muted)]'>
          Search responses, then open a full profile page for results and charts.
        </p>
      </div>

      <Card className='overflow-hidden border border-[var(--fb-border)] bg-[var(--fb-surface)] shadow-[var(--fb-shadow-sm)]'>
        <CardHeader className='gap-4 md:flex-row md:items-center md:justify-between'>
          <div>
            <CardTitle className='text-lg text-[var(--fb-text-strong)] md:text-xl'>
              Responses
            </CardTitle>
            <CardDescription>
              {isLoading
                ? 'Loading submissions…'
                : `${filteredUsers.length} of ${users.length} submissions`}
            </CardDescription>
          </div>
          <Button
            variant='outline'
            onClick={downloadAllUsersCSV}
            disabled={isLoading || users.length === 0}
            className='rounded-xl'
          >
            <Download className='h-4 w-4' />
            Download all
          </Button>
        </CardHeader>
        <CardContent className='flex flex-col gap-4'>
          <div className='relative w-full'>
            <Search className='pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
            <Input
              placeholder='Search by name or phone number'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className='pl-10'
              aria-label='Search by name or phone number'
            />
          </div>

          <div className='overflow-x-auto'>
            <Table className='min-w-[880px]'>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Mobile number</TableHead>
                  <TableHead>
                    <SortButton
                      label='Status'
                      active={sortState.status}
                      onClick={handleSortByStatus}
                    />
                  </TableHead>
                  <TableHead>
                    <SortButton
                      label='Submitted on'
                      active={sortState.createdOn}
                      onClick={() => handleSortByDate('createdOn')}
                    />
                  </TableHead>
                  <TableHead>
                    <SortButton
                      label='Updated on'
                      active={sortState.submittedOn}
                      onClick={() => handleSortByDate('submittedOn')}
                    />
                  </TableHead>
                  <TableHead className='text-right'>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading &&
                  Array.from({ length: 5 }).map((_, index) => (
                    <TableRow key={`skeleton-${index}`}>
                      <TableCell colSpan={6}>
                        <Skeleton className='h-10 w-full' />
                      </TableCell>
                    </TableRow>
                  ))}

                {!isLoading && filteredUsers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className='py-10 text-center text-muted-foreground'>
                      No submissions match your search.
                    </TableCell>
                  </TableRow>
                )}

                {!isLoading &&
                  filteredUsers.map((user) => {
                    const { date: createdDate, time: createdTime } = getDateAndTime(
                      user.createdTimestamp,
                    )
                    const { date: updatedDate, time: updatedTime } = getDateAndTime(
                      user.updatedTimestamp,
                    )
                    const version = getQuestionnaireVersion(user.userResponse, user)

                    return (
                      <TableRow key={user.id}>
                        <TableCell className='font-medium text-[var(--fb-text-strong)]'>
                          {`${user.firstName || ''} ${user.lastName || ''}`.trim() || '—'}
                        </TableCell>
                        <TableCell>{user.phoneNumber}</TableCell>
                        <TableCell>
                          <div className='flex flex-wrap items-center gap-2'>
                            <Badge variant={user.isCompleted ? 'soft' : 'outline'}>
                              {user.isCompleted ? 'Completed' : 'Pending'}
                            </Badge>
                            {version === 2 ? <Badge variant='outline'>v2</Badge> : null}
                          </div>
                        </TableCell>
                        <TableCell className='text-muted-foreground'>
                          {createdDate} / {createdTime}
                        </TableCell>
                        <TableCell className='text-muted-foreground'>
                          {updatedDate} / {updatedTime}
                        </TableCell>
                        <TableCell className='text-right'>
                          <Button variant='outline' size='sm' className='rounded-xl' asChild>
                            <Link
                              to={firstUserIdeaDetailPath(user.phoneNumber)}
                              state={{ selectedUser: user }}
                            >
                              View more
                            </Link>
                          </Button>
                        </TableCell>
                      </TableRow>
                    )
                  })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default FirstUserIdea
